import { useState } from "react";
import t from "@/lang/lang";
import {
  Offer,
  UpdateFlyExterminatorMaintenanceDiscountRequest,
} from "@/services/backend/qpm/offers/types/offer";
import { useQpmOfferUpdateFlyExterminatorMaintenanceDiscountMutation } from "@/services/backend/qpm/offers/api/offer-api";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import {
  FloatNumberInput,
  Value,
} from "@/shared/components/domain/qpm/inputs/number-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  fieldErrorsForPrefix,
  resetField,
} from "@/shared/lib/errors/validation-error";
import { useOfferContext } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/offer-context";
import { InfoIconTooltip } from "@/shared/components/domain/qpm/info-icon-tooltip";
import { resolvedPriceVisibility } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/price-visibility";

type Props = {
  offer: Offer;
};

export function OfferFlyExterminatorMaintenanceComponent({ offer }: Props) {
  const visibility = resolvedPriceVisibility(offer);
  const hiddenBySetting = !visibility.maintenance.fiv;
  const { validationError, setValidationError } = useOfferContext();
  const [request, setRequest] =
    useState<UpdateFlyExterminatorMaintenanceDiscountRequest>({
      offerId: offer.id,
      discount: offer.flyExterminatorMaintenance.discount,
    });
  const [updateDiscount, { error, isLoading, isSuccess, reset }] =
    useQpmOfferUpdateFlyExterminatorMaintenanceDiscountMutation();

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    updateDiscount,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
    { toastError: true, skip: offer.immutable },
  );

  const cohortErrors = fieldErrorsForPrefix(
    validationError,
    "flyExterminatorMaintenance.",
  );

  const onDiscountChange = (value: number) => {
    let discount = 0;
    if (value > 0) {
      discount = value / 100;
    }

    setValidationError(
      resetField(validationError, "flyExterminatorMaintenance.discount"),
    );
    setRequest((prev) => ({
      ...prev,
      discount,
    }));
  };

  return (
    <div className="flex flex-col gap-3">
      {offer.flyExterminatorMaintenance.cohorts.length === 0 ? (
        <div className="text-muted-foreground rounded-md border border-dashed px-3 py-4 text-sm">
          {t("Keine FIV-Wartung vorhanden")}
        </div>
      ) : (
        <Table
          className={`min-w-[620px] ${hiddenBySetting ? "opacity-50" : ""}`}
        >
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead className="h-10 px-2 py-2 font-semibold">
                {t("Position")}
              </TableHead>
              <TableHead className="px-2 py-2 text-end font-semibold">
                {t("Maßnahmen")}
              </TableHead>
              <TableHead className="px-2 py-2 text-end font-semibold">
                {t("Preis pro Maßnahme")}
              </TableHead>
              <TableHead className="px-2 py-2 text-end font-semibold">
                {t("Preis mit Rabatt")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {offer.flyExterminatorMaintenance.cohorts.map((cohort) => (
              <TableRow
                key={`${cohort.monitorAmount}-${cohort.amountOfMeasures}-${cohort.adhesiveCadence}-${cohort.tubeCadence}`}
              >
                <TableCell className="px-2 py-2.5 text-sm font-medium sm:text-base">{`${t("Wartung von")} ${cohort.monitorAmount} ${t("Stück")}`}</TableCell>
                <TableCell className="px-2 py-2.5 text-end">
                  <Value
                    value={cohort.amountOfMeasures}
                    fractions={0}
                    className="tabular-nums"
                  />
                </TableCell>
                <TableCell className="px-2 py-2.5 text-end">
                  <Value
                    value={cohort.pricePerMeasure}
                    fractions={2}
                    className="tabular-nums"
                  />
                </TableCell>
                <TableCell className="px-2 py-2.5 text-end">
                  <Value
                    value={cohort.discountedPricePerMeasure}
                    fractions={2}
                    className="tabular-nums"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <div
        className={`flex items-center justify-end gap-2 ${hiddenBySetting ? "opacity-50" : ""}`}
      >
        {hiddenBySetting ? (
          <InfoIconTooltip
            text={t(
              "Diese Position ist im PDF ausgeblendet, da sie in den Preispositionen deaktiviert wurde.",
            )}
          />
        ) : null}
        <span className="text-sm">{t("Rabatt für FIV-Wartung")}</span>
        <FloatNumberInput
          className="w-24"
          value={offer.flyExterminatorMaintenance.discount * 100}
          onChange={onDiscountChange}
          disabled={offer.immutable || hiddenBySetting}
        />
        <span className="text-sm">%</span>
      </div>
      {cohortErrors?.discount?.length ? (
        <div className="text-destructive text-right text-xs">
          {cohortErrors.discount.join(" ")}
        </div>
      ) : null}
    </div>
  );
}
